import { useAppTranslation } from '../i18n/useAppTranslation';
import { Box, Container, Typography, Button, Divider, Link as MuiLink } from '@mui/material';
import { ArrowBack, OpenInNew } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { usePageMeta } from '../utils/usePageMeta';

/**
 * Pages legales de G1Sport.
 *
 * IMPORTANT : ces textes constituent une base solide et standard pour une
 * place de marche de coaching sportif exploitee par la societe DevAntiq, mais
 * ils doivent etre relus et valides par un conseil juridique avant mise en
 * ligne definitive. Les emplacements entre crochets [ ] sont a completer avec
 * les informations reelles de la societe.
 */

const MAJ = new Date(2026, 6, 23);

type Section = { title: string; paragraphs: string[] };
type Doc = { slug: string; title: string; sections: Section[] };

const MENTIONS: Doc = {
  slug: 'mentions-legales',
  title: 'Mentions légales',
  sections: [
    {
      title: '1. Éditeur du site',
      paragraphs: [
        "Le site et l'application G1Sport sont édités par la société DevAntiq.",
        "Forme juridique et capital social : [à compléter].",
        "Siège social : [adresse complète du siège].",
        "SIREN / SIRET : [numéro à compléter] — RCS : [ville d'immatriculation].",
        "N° de TVA intracommunautaire : [à compléter].",
        "Contact : contact@g1sport.fr.",
      ],
    },
    {
      title: '2. Directeur de la publication',
      paragraphs: ["Le directeur de la publication est le représentant légal de la société DevAntiq."],
    },
    {
      title: '3. Hébergement',
      paragraphs: [
        "Le site est hébergé par [nom de l'hébergeur], [adresse de l'hébergeur], téléphone : [téléphone de l'hébergeur].",
        "Si vous hébergez chez IONOS : IONOS SARL, 7 place de la Gare, BP 70109, 57201 Sarreguemines Cedex, France.",
      ],
    },
    {
      title: '4. Propriété intellectuelle',
      paragraphs: [
        "L'ensemble des éléments composant le site (structure, textes, logo, graphismes, visuels) est protégé par le droit de la propriété intellectuelle. Toute reproduction ou représentation, totale ou partielle, sans autorisation écrite préalable, est interdite.",
      ],
    },
    {
      title: '5. Contact',
      paragraphs: ["Pour toute question relative au site, vous pouvez nous écrire à contact@g1sport.fr."],
    },
  ],
};

const CONFIDENTIALITE: Doc = {
  slug: 'confidentialite',
  title: 'Politique de confidentialité',
  sections: [
    {
      title: '1. Responsable du traitement',
      paragraphs: [
        "Le responsable du traitement des données est la société DevAntiq, éditrice de G1Sport (voir les mentions légales). Contact : contact@g1sport.fr.",
      ],
    },
    {
      title: '2. Données que nous collectons',
      paragraphs: [
        "Données de compte : nom d'utilisateur, adresse e-mail, mot de passe (stocké de façon chiffrée), rôle (coach ou sportif).",
        "Données de profil : pour les coachs, biographie, spécialités, sport, tarif, ville, diplômes, coordonnées bancaires (IBAN/BIC, jamais affichées publiquement) et liens de réseaux sociaux ; pour les sportifs, sport, taille, poids, objectifs et informations médicales que vous choisissez de renseigner.",
        "Données d'activité : réservations, disponibilités, évaluations de performance, messages échangés, historique de paiements déclarés.",
        "Données techniques : adresse IP et journaux de connexion, à des fins de sécurité et de bon fonctionnement.",
      ],
    },
    {
      title: '3. Finalités et bases légales',
      paragraphs: [
        "Fournir le service de mise en relation et de suivi (exécution du contrat).",
        "Gérer les comptes, l'authentification et la sécurité (intérêt légitime).",
        "Gérer les abonnements et la facturation via notre prestataire de paiement (exécution du contrat et obligation légale).",
        "Vous envoyer des e-mails liés au service : confirmation d'inscription, réservations, réinitialisation de mot de passe (exécution du contrat).",
      ],
    },
    {
      title: '4. Destinataires et sous-traitants',
      paragraphs: [
        "Vos données ne sont jamais vendues. Elles peuvent être transmises à des sous-traitants strictement nécessaires au service : Stripe (paiement des abonnements), Brevo (envoi des e-mails transactionnels), et notre hébergeur.",
        "Les informations bancaires liées aux paiements d'abonnement sont traitées directement par Stripe et ne transitent pas par nos serveurs.",
      ],
    },
    {
      title: '5. Durée de conservation',
      paragraphs: [
        "Les données de compte sont conservées tant que le compte est actif, puis supprimées ou anonymisées dans un délai raisonnable après sa fermeture. Les données de facturation sont conservées pour la durée légale applicable (10 ans pour les pièces comptables).",
      ],
    },
    {
      title: '6. Vos droits',
      paragraphs: [
        "Conformément au RGPD et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, de portabilité et d'opposition sur vos données.",
        "Pour exercer ces droits, écrivez à contact@g1sport.fr. Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).",
      ],
    },
    {
      title: '7. Cookies et stockage local',
      paragraphs: [
        "G1Sport utilise le stockage local de votre navigateur pour maintenir votre session connectée : ce mécanisme est strictement nécessaire au fonctionnement et n'est pas utilisé à des fins publicitaires. Voir notre politique de cookies pour le détail.",
      ],
    },
  ],
};

const CGV: Doc = {
  slug: 'cgv',
  title: "Conditions générales d'utilisation et de vente",
  sections: [
    {
      title: '1. Objet',
      paragraphs: [
        "G1Sport est une plateforme mettant en relation des coachs sportifs et des sportifs. Elle permet de présenter des profils, de gérer des disponibilités et des réservations, d'échanger par messagerie et de suivre des performances.",
        "Les présentes conditions régissent l'utilisation du service. En créant un compte, vous les acceptez.",
      ],
    },
    {
      title: '2. Inscription',
      paragraphs: [
        "L'inscription est gratuite pour les sportifs. Chaque utilisateur s'engage à fournir des informations exactes et à préserver la confidentialité de ses identifiants.",
      ],
    },
    {
      title: '3. Rôle de la plateforme',
      paragraphs: [
        "G1Sport est un intermédiaire technique de mise en relation. Le règlement des séances s'effectue directement entre le sportif et le coach, en dehors de la plateforme ; G1Sport n'encaisse pas le prix des séances et n'est pas partie au contrat de coaching conclu entre eux.",
        "Le coach est seul responsable de la prestation de coaching, de sa conformité et de ses obligations légales, fiscales et sociales.",
      ],
    },
    {
      title: "4. Abonnement des coachs",
      paragraphs: [
        "L'accès à l'espace Coach Pro fait l'objet d'un abonnement de 19,90 € par mois, précédé d'une période d'essai de 6 mois offerte.",
        "L'abonnement est sans engagement et résiliable à tout moment ; la résiliation prend effet à la fin de la période déjà réglée. Le paiement est géré par Stripe.",
      ],
    },
    {
      title: '5. Droit de rétractation',
      paragraphs: [
        "Conformément au Code de la consommation, le consommateur dispose d'un délai de rétractation de 14 jours pour les services souscrits à distance. Pour l'abonnement, la période d'essai gratuite de 6 mois couvre largement ce délai. [À faire valider selon votre modèle exact.]",
      ],
    },
    {
      title: '6. Responsabilité',
      paragraphs: [
        "G1Sport s'efforce d'assurer la disponibilité et la sécurité du service mais ne peut garantir une absence totale d'interruption. Sa responsabilité ne saurait être engagée pour les contenus publiés par les utilisateurs ni pour la relation entre coach et sportif.",
      ],
    },
    {
      title: '7. Données personnelles',
      paragraphs: [
        "Le traitement des données personnelles est décrit dans notre politique de confidentialité, qui fait partie intégrante des présentes conditions.",
      ],
    },
    {
      title: '8. Droit applicable et litiges',
      paragraphs: [
        "Les présentes conditions sont soumises au droit français. En cas de litige, une solution amiable sera recherchée en priorité. À défaut, le consommateur peut recourir gratuitement à un médiateur de la consommation [préciser le médiateur retenu] avant toute action judiciaire.",
      ],
    },
  ],
};

const COOKIES: Doc = {
  slug: 'cookies',
  title: 'Politique de cookies',
  sections: [
    {
      title: '1. Qu\'utilisons-nous ?',
      paragraphs: [
        "G1Sport limite volontairement l'usage de traceurs. Nous utilisons le stockage local du navigateur pour conserver votre session connectée (jeton d'authentification). Ce mécanisme est strictement nécessaire au fonctionnement du service.",
      ],
    },
    {
      title: '2. Mesure d\'audience',
      paragraphs: [
        "Avec votre accord pour la mesure d’audience, G1Sport active Umami et des statistiques de pages vues, sessions et temps actif. Le suivi des sessions utilise des identifiants pseudonymes stockés dans le navigateur. Aucun mot de passe ni contenu de formulaire n’est collecté.",
      ],
    },
    {
      title: '3. Mesure publicitaire (Meta Pixel) — soumise à consentement',
      paragraphs: [
        "Sur le site web uniquement, et seulement si vous activez « Publicité Meta » ou cliquez sur « Tout accepter » dans les préférences de confidentialité, nous activons le Meta Pixel (Meta Platforms Ireland) afin de mesurer l'efficacité de nos campagnes publicitaires. Tant que vous n'avez pas accepté, ce traceur n'est pas chargé.",
        "Si vous cliquez sur « Tout refuser », aucun traceur publicitaire n'est activé. Le Meta Pixel n'est jamais chargé dans l'application mobile.",
        "Vous pouvez revenir sur votre choix à tout moment depuis les pages Cookies ou Confidentialité, avec « Modifier mes préférences de confidentialité ». Nous ne vendons pas vos données de navigation.",
      ],
    },
    {
      title: '4. Gestion',
      paragraphs: [
        "Vous pouvez à tout moment modifier ou retirer votre accord depuis les pages Cookies ou Confidentialité, avec « Modifier mes préférences de confidentialité ». Vous pouvez aussi vider le stockage local et les cookies depuis les réglages de votre navigateur. La suppression du jeton de session vous déconnectera simplement de l'application.",
      ],
    },
  ],
};

export const LEGAL_DOCS: Record<string, Doc> = {
  'mentions-legales': MENTIONS,
  'confidentialite': CONFIDENTIALITE,
  'cgv': CGV,
  'cookies': COOKIES,
};

const LegalPage = ({ slug }: { slug: string }) => {
  const { tr, locale } = useAppTranslation();
  const navigate = useNavigate();
  const doc = LEGAL_DOCS[slug];
  usePageMeta(
    doc ? `${tr(doc.title)} — G1Sport` : 'Document introuvable — G1Sport',
    doc ? `${tr(doc.title)} de G1Sport, plateforme de mise en relation entre coachs sportifs et sportifs.` : undefined,
  );

  if (!doc) {
    return (
      <Container sx={{ pt: { xs: 'calc(24px + env(safe-area-inset-top))', md: 8 }, pb: 8 }}>
        <Typography variant="h5">{tr("Document introuvable.")}</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mt: 2 }}>{tr("Retour à l'accueil")}</Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: { xs: 'calc(24px + env(safe-area-inset-top))', md: 6 }, pb: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mb: 3, fontWeight: 700 }}>
           {tr("Retour à l'accueil")} </Button>

        <Typography variant="h4" fontWeight={800} gutterBottom>{tr(doc.title)}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
           {tr("Dernière mise à jour :")} {new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(MAJ)}
        </Typography>

        {doc.sections.map((s) => (
          <Box key={tr(s.title)} sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>{tr(s.title)}</Typography>
            {s.paragraphs.map((p, i) => (
              <Typography key={i} color="text.secondary" sx={{ mb: 1.2, lineHeight: 1.7 }}>{tr(p)}</Typography>
            ))}
          </Box>
        ))}

        {/* Renvoi vers le site vitrine de l'editeur (DevAntiq). Ouvre le navigateur
            systeme sur natif comme sur web. Ce n'est pas un lien de paiement. */}
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.2 }}>
             {tr("G1Sport est édité par DevAntiq.")} </Typography>
          <Button
            variant="outlined"
            endIcon={<OpenInNew />}
            onClick={() => window.open('https://devantiq.com', '_blank', 'noopener,noreferrer')}
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
          >
             {tr("Visiter devantiq.com")} </Button>
        </Box>

        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary">
           {tr("Voir aussi :")}{' '}
          <MuiLink component={RouterLink} to="/mentions-legales">{tr("Mentions légales")}</MuiLink>{' · '}
          <MuiLink component={RouterLink} to="/confidentialite">{tr("Confidentialité")}</MuiLink>{' · '}
          <MuiLink component={RouterLink} to="/cgv">{tr("CGU / CGV")}</MuiLink>{' · '}
          <MuiLink component={RouterLink} to="/cookies">{tr("Cookies")}</MuiLink>
        </Typography>
      </Container>
    </Box>
  );
};

export default LegalPage;
